import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Archive, Briefcase, CalendarDays, CalendarOff, Inbox, Loader2, LogOut, RefreshCw, Trash2 } from "lucide-react";
import Seo from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { LogoMark } from "@/components/Logo";
import { prettySlot } from "@/data/content";

const TOKEN_KEY = "brightside_admin_token";
const ENQUIRY_STATUSES = ["new", "contacted", "scheduled", "archived"];
const CAREER_STATUSES = ["new", "reviewed", "archived"];

const STATUS_STYLES = {
  new: "bg-terracotta/15 text-terracotta-deep",
  contacted: "bg-sage-tint text-forest",
  scheduled: "bg-sage/15 text-sage",
  reviewed: "bg-sage-tint text-forest",
  archived: "bg-pebble/10 text-pebble",
};

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Could not sign in — please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-linen px-4">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md rounded-[2rem] border border-sand bg-white p-10 shadow-[0_24px_60px_rgba(28,56,41,0.12)]">
        <LogoMark className="h-14 w-14" />
        <h1 className="mt-6 font-serif text-3xl text-forest">Team login</h1>
        <p className="mt-2 text-sm text-pebble">Brightside admin — enquiries & applications.</p>
        <div className="mt-8 space-y-4">
          <div>
            <label htmlFor="admin-email" className="mb-2 block text-sm font-semibold text-forest">Email</label>
            <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl border-sand" autoComplete="username" />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block text-sm font-semibold text-forest">Password</label>
            <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl border-sand" autoComplete="current-password" />
          </div>
        </div>
        {error && <p data-testid="admin-login-error" className="mt-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}
        <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-forest py-6 text-linen hover:bg-terracotta">
          {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</>) : "Sign in"}
        </Button>
      </form>
    </div>
  );
};

const ItemCard = ({ item, type, onStatus, onDelete }) => {
  const statuses = type === "enquiries" ? ENQUIRY_STATUSES : CAREER_STATUSES;
  const [saving, setSaving] = useState(false);

  const change = async (e) => {
    const status = e.target.value;
    setSaving(true);
    try {
      await api.patch(`/${type}/${item.id}`, { status });
      onStatus(item.id, status);
    } catch {
      /* keep previous status on failure */
    } finally {
      setSaving(false);
    }
  };

  return (
    <article data-testid={`admin-${type}-item-${item.id}`} className="rounded-3xl border border-sand bg-white p-6 sm:p-7">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="font-serif text-lg text-forest">{item.name}</h3>
            <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${STATUS_STYLES[item.status] || ""}`} data-testid={`admin-item-status-${item.id}`}>
              {item.status}
            </span>
            {item.kind && item.kind !== "general" && (
              <span className="rounded-full bg-linen-deep px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-pebble">{item.kind}</span>
            )}
          </div>
          <p className="mt-1 text-xs text-pebble/70">{format(new Date(item.created_at), "d MMM yyyy · HH:mm")}</p>
        </div>
        <div className="flex items-center gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin text-sage" />}
          <select
            data-testid={`admin-status-select-${item.id}`}
            value={item.status}
            onChange={change}
            className="h-10 rounded-xl border border-sand bg-linen px-3 text-xs font-medium text-forest focus:outline-none focus:ring-2 focus:ring-sage"
          >
            {statuses.map((s) => (
              <option key={s} value={s}>Mark as {s}</option>
            ))}
          </select>
          <button
            data-testid={`admin-delete-${item.id}`}
            onClick={async () => {
              if (!window.confirm(`Delete this ${type === "enquiries" ? "enquiry" : "application"} from ${item.name}? This can't be undone.`)) return;
              try {
                await api.delete(`/${type}/${item.id}`);
                onDelete(item.id);
                toast.success("Deleted.");
              } catch {
                toast.error("Could not delete — please try again.");
              }
            }}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-sand text-pebble transition-colors hover:border-terracotta hover:bg-terracotta/10 hover:text-terracotta"
            aria-label="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mt-4 grid gap-2 text-sm text-pebble sm:grid-cols-2">
        <p><span className="font-semibold text-forest">Email:</span> <a href={`mailto:${item.email}`} className="underline-offset-2 hover:underline">{item.email}</a></p>
        {item.phone && <p><span className="font-semibold text-forest">Phone:</span> {item.phone}</p>}
        {item.service_interest && <p><span className="font-semibold text-forest">Interested in:</span> {item.service_interest}</p>}
        {item.organisation && <p><span className="font-semibold text-forest">Organisation:</span> {item.organisation}</p>}
        {item.role && <p><span className="font-semibold text-forest">Role:</span> {item.role}</p>}
        {item.pvg && <p><span className="font-semibold text-forest">PVG:</span> {item.pvg}</p>}
        {item.availability && <p><span className="font-semibold text-forest">Availability:</span> {item.availability}</p>}
        {item.preferred_date && (
          <p data-testid={`admin-item-slot-${item.id}`}>
            <span className="font-semibold text-forest">Preferred call back:</span>{" "}
            {format(new Date(`${item.preferred_date}T00:00:00`), "EEE d MMM yyyy")} at {prettySlot(item.preferred_slot)}
          </p>
        )}
      </div>
      {item.message && <p className="mt-4 rounded-2xl bg-linen p-4 text-sm leading-relaxed text-ink/85">{item.message}</p>}
    </article>
  );
};

const AvailabilityPanel = () => {
  const [date, setDate] = useState(undefined);
  const [blocks, setBlocks] = useState([]);
  const [dayInfo, setDayInfo] = useState({ slots: [], booked: [], blocked: [] });
  const [saving, setSaving] = useState(false);

  const loadBlocks = useCallback(async () => {
    try {
      const { data } = await api.get("/availability/blocks");
      setBlocks(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not load blocked days."));
    }
  }, []);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  const dateKey = date ? format(date, "yyyy-MM-dd") : null;

  useEffect(() => {
    if (!dateKey) return;
    api
      .get("/enquiries/availability", { params: { date: dateKey } })
      .then(({ data }) => setDayInfo(data))
      .catch(() => setDayInfo({ slots: [], booked: [], blocked: [] }));
  }, [dateKey, blocks]);

  const dayBlocks = dateKey ? blocks.filter((b) => b.date === dateKey) : [];
  const wholeDayBlock = dayBlocks.find((b) => !b.slot);

  const toggleSlot = async (slot) => {
    if (!dateKey || saving) return;
    setSaving(true);
    try {
      const existing = dayBlocks.find((b) => b.slot === slot);
      if (existing) {
        await api.delete(`/availability/blocks/${existing.id}`);
        toast.success(`${slot} on ${format(date, "d MMM")} is bookable again.`);
      } else {
        await api.post("/availability/blocks", { date: dateKey, slot });
        toast.success(`${slot} on ${format(date, "d MMM")} is now blocked.`);
      }
      await loadBlocks();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not update that slot."));
    } finally {
      setSaving(false);
    }
  };

  const toggleWholeDay = async () => {
    if (!dateKey || saving) return;
    setSaving(true);
    try {
      if (wholeDayBlock) {
        await api.delete(`/availability/blocks/${wholeDayBlock.id}`);
        toast.success(`${format(date, "EEEE d MMM")} is open for bookings again.`);
      } else {
        await api.post("/availability/blocks", { date: dateKey, slot: null });
        toast.success(`${format(date, "EEEE d MMM")} is now blocked.`);
      }
      await loadBlocks();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not update that day."));
    } finally {
      setSaving(false);
    }
  };

  const todayKey = format(new Date(), "yyyy-MM-dd");
  const upcoming = blocks.filter((b) => b.date >= todayKey);

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="availability-panel">
      <div className="rounded-3xl border border-sand bg-white p-6 sm:p-8">
        <h2 className="font-serif text-xl text-forest">Block a day or time</h2>
        <p className="mt-1.5 text-sm text-pebble">
          Pick a date, then block the whole day or individual times. Families see blocked times as unavailable instantly.
        </p>
        <div className="mt-5 w-fit rounded-2xl border border-sand bg-linen p-2">
          <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} />
        </div>
        {date && (
          <div className="mt-5" data-testid="availability-day-editor">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-widest text-pebble">{format(date, "EEEE d MMMM")}</p>
              <button
                data-testid="block-whole-day-button"
                onClick={toggleWholeDay}
                disabled={saving}
                className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  wholeDayBlock
                    ? "bg-terracotta text-linen"
                    : "border border-sand bg-white text-forest hover:border-terracotta hover:text-terracotta"
                }`}
              >
                {wholeDayBlock ? "Day blocked — unblock" : "Block whole day"}
              </button>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2.5 sm:grid-cols-5">
              {dayInfo.slots.map((s) => {
                const isBooked = dayInfo.booked.includes(s);
                const isBlocked = Boolean(wholeDayBlock) || dayBlocks.some((b) => b.slot === s);
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`availability-slot-${s.replace(":", "")}`}
                    onClick={() => toggleSlot(s)}
                    disabled={saving || isBooked || Boolean(wholeDayBlock)}
                    title={isBooked ? "A family has booked this time" : undefined}
                    className={`rounded-full border px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                      isBooked
                        ? "cursor-not-allowed border-terracotta/40 bg-terracotta/10 text-terracotta-deep"
                        : isBlocked
                          ? "border-sand bg-linen-deep text-pebble/50 line-through"
                          : "border-sand bg-white text-forest hover:border-terracotta hover:text-terracotta"
                    }`}
                  >
                    {prettySlot(s)}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-pebble/70">Terracotta times already have a family booked and can't be blocked.</p>
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-sand bg-white p-6 sm:p-8">
        <h2 className="font-serif text-xl text-forest">Upcoming time off</h2>
        <p className="mt-1.5 text-sm text-pebble">Days and times currently blocked from bookings.</p>
        <div className="mt-5 space-y-3" data-testid="blocked-list">
          {upcoming.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-sand bg-linen/60 px-5 py-10 text-center">
              <CalendarOff className="mx-auto h-7 w-7 text-pebble/40" />
              <p className="mt-3 text-sm text-pebble">Nothing blocked — every slot is bookable.</p>
            </div>
          ) : (
            upcoming.map((b) => (
              <div key={b.id} data-testid={`blocked-item-${b.id}`} className="flex items-center justify-between rounded-2xl border border-sand bg-linen px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <CalendarDays className="h-4 w-4 text-terracotta" />
                  <span className="text-sm font-medium text-forest">
                    {format(new Date(`${b.date}T00:00:00`), "EEE d MMM yyyy")}
                    <span className="ml-2 text-pebble">{b.slot ? `at ${prettySlot(b.slot)}` : "· whole day"}</span>
                  </span>
                </div>
                <button
                  data-testid={`blocked-remove-${b.id}`}
                  onClick={async () => {
                    await api.delete(`/availability/blocks/${b.id}`);
                    await loadBlocks();
                    toast.success("Block removed.");
                  }}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-pebble transition-colors hover:bg-terracotta/10 hover:text-terracotta"
                  aria-label="Remove block"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("enquiries");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    if (tab === "availability") return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/${tab}`, { params: tab === "enquiries" && statusFilter ? { status: statusFilter } : {} });
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
        return;
      }
      setError(formatApiError(err.response?.data?.detail, "Could not load — please refresh."));
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const newCount = items.filter((i) => i.status === "new").length;

  return (
    <div className="min-h-screen bg-linen" data-testid="admin-dashboard">
      <header className="border-b border-sand bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-5 sm:px-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-10 w-10" />
            <div>
              <h1 className="font-serif text-xl text-forest">Brightside admin</h1>
              <p className="text-xs text-pebble">
                {tab === "availability" ? "Manage your bookable days" : `${newCount} new ${tab === "enquiries" ? "enquiries" : "applications"}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <button data-testid="admin-refresh-button" onClick={load} className="flex h-10 w-10 items-center justify-center rounded-full border border-sand text-forest transition-colors hover:bg-sage-tint" aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <Button data-testid="admin-logout-button" variant="outline" onClick={onLogout} className="rounded-full border-sand text-forest hover:bg-terracotta hover:text-linen">
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2 rounded-full border border-sand bg-white p-1.5">
            {[
              { key: "enquiries", label: "Enquiries", icon: Inbox },
              { key: "careers", label: "Applications", icon: Briefcase },
              { key: "availability", label: "Availability", icon: CalendarDays },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => { setTab(t.key); setStatusFilter(""); }}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === t.key ? "bg-forest text-linen" : "text-pebble hover:text-forest"}`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>
          {tab === "enquiries" && (
            <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
              {["", ...ENQUIRY_STATUSES].map((s) => (
                <button
                  key={s || "all"}
                  data-testid={`admin-filter-${s || "all"}`}
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${statusFilter === s ? "bg-terracotta text-linen" : "border border-sand bg-white text-pebble hover:text-forest"}`}
                >
                  {s || "All"}
                </button>
              ))}
            </div>
          )}
        </div>

        {tab === "availability" ? (
          <div className="mt-8">
            <AvailabilityPanel />
          </div>
        ) : (
        <div className="mt-8 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-pebble" data-testid="admin-loading">
              <Loader2 className="mr-3 h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <p className="rounded-2xl bg-destructive/10 px-6 py-5 text-sm text-destructive" data-testid="admin-error">{error}</p>
          ) : items.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-sand bg-white/60 px-6 py-20 text-center" data-testid="admin-empty-state">
              <Archive className="mx-auto h-8 w-8 text-pebble/40" />
              <p className="mt-4 font-serif text-xl text-forest">Nothing here yet</p>
              <p className="mt-1.5 text-sm text-pebble">New {tab === "enquiries" ? "enquiries" : "applications"} will appear here the moment they arrive.</p>
            </div>
          ) : (
            items.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                type={tab}
                onStatus={(id, status) => setItems((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)))}
                onDelete={(id) => setItems((prev) => prev.filter((p) => p.id !== id))}
              />
            ))
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <>
      <Seo title="Team login — Brightside Befriending Services" siteName="Brightside Befriending Services" />
      {token ? <Dashboard onLogout={logout} /> : <LoginCard onLogin={setToken} />}
    </>
  );
}
